import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {FacebookLoginProvider, GoogleLoginProvider, SocialAuthService, SocialUser} from 'angularx-social-login';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';

import { INewUser, IUser, IBadge, ICert, IRequestLogin } from '../models/auth.model';
import {HttpService} from './http.service';
import {MessagingService} from './messaging.service';
import {ThemeName, ThemeService} from "./theme.service";

export interface OtpVerify {
  "email": string;
  "code": string;
  "token": string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _authUser$ = new BehaviorSubject<IUser | undefined>(undefined);
  authUser = this._authUser$.asObservable();
  socialAuthState = new Subject<SocialUser>();

  returnUrl = '/';

  recapcha = '';

  constructor(
    private httpService: HttpService,
    private router: Router,
    private messagingService: MessagingService,
    private socialAuthService: SocialAuthService,
    private theme: ThemeService
  ) { }

  signup(bodyData: INewUser): Observable<any> {
    let payload = bodyData;
    if (bodyData.phone) {
      payload = { ...payload, phone: bodyData.phone.trim() };
    }
    if (bodyData.email) {
      payload = { ...payload, email: bodyData.email.trim() };
    }

    return this.httpService
      .post('/student/register', payload);
  }

  login(request: IRequestLogin): Observable<any> {
    let payload = request;
    if (request.phone) {
      payload = { ...payload, phone: request.phone.trim() };
    }
    if (request.email) {
      payload = { ...payload, email: request.email.trim() };
    }

    return this.httpService
      .post('/student/login', payload)
      .pipe(
        tap(
          (res) => {
            this.handleAuthentication(res.user, res.accessToken);
            this.navigateAfterLogin();
          }
        )
      )
  }

  private handleAuthentication(user: IUser, accessToken: string) {
    this.updateUserInfo(user);
    localStorage.setItem('student_accessToken', accessToken);
  }

  updateUserInfo(user: IUser) {
    this._authUser$.next(user);
    localStorage.setItem('student_user', JSON.stringify(user));
  }

  get accessToken(): string | null {
    return localStorage.getItem('student_accessToken');
  }

  get provider(): string | null {
    const user = localStorage.getItem('student_user');
    if (!user) {
      return null;
    }
    return JSON.parse(user).provider;
  }

  navigateAfterLogin(): void {
    this.router.navigate([this.returnUrl]).then(() => {
      this.returnUrl = '/';
    });
  }

  autoLogin() {
    const userData = localStorage.getItem('student_user');
    if (!userData) {
      return;
    }
    this._authUser$.next(JSON.parse(userData));
  }

  logout(): void {
    this._authUser$.next(undefined);
    this.theme.selectThemeByName(ThemeName.LIGHT_Manrope);
    if (this.provider) {
      this.signOutSocial();
    }
    localStorage.removeItem('student_user');
    localStorage.removeItem('student_accessToken');
    this.router.navigateByUrl('login');
    this.messagingService.deleteToken();
  }

  setValueRecapcha(value: string) {
    this.recapcha = value;
  }

  getValueRecapcha() {
    return this.recapcha;
  }

  getInfo(): Observable<IUser> {
    return this.httpService.get('/student/me');
  }

  getBadge(): Observable<IBadge> {
    return this.httpService.get('/student/dashboard/badge');
  }

  getCert(): Observable<ICert[]> {
    return this.httpService.get('/student/dashboard/cert');
  }

  editInfo(bodyData: any): Observable<any> {
    return this.httpService.put('/student', bodyData);
  }

  getVerifyCode(email: string): Observable<any> {
    return this.httpService.post('/student/request-forgot-password', { email });
  }

  changePassword(email: string, code: string, newPassword: string, newPasswordConfirmation: string): Observable<any> {
    return this.httpService.post('/student/set-forgot-password', { email, code, newPassword, newPasswordConfirmation });
  }

  signInWithFb(): void {
    this.socialAuthService
      .signIn(FacebookLoginProvider.PROVIDER_ID, {
        scope: 'public_profile,email',
        auth_type: 'rerequest'
      })
      .then((user) => {
        this.socialAuthState.next(user);
      });
  }

  signInWithGg(): void {
    this.socialAuthService
      .signIn(GoogleLoginProvider.PROVIDER_ID)
      .then((user) => {
        this.socialAuthState.next(user);
      });
  }

  trackSocialUser() {
    return this.socialAuthState
      .pipe(
        switchMap((user: SocialUser) => this.verifySocialToken(user.authToken, user.provider)),
        tap(
          (res) => {
            this.handleAuthentication(res.user, res.accessToken);
            this.navigateAfterLogin();
          }
        )
      );
  }

  verifySocialToken(snsToken: string, snsType: string) {
    return this.httpService.post('/student/login-social', { snsToken, snsType});
  }

  signOutSocial(): void {
    this.socialAuthService.signOut(true).then();
  }

  resendOtp(payload: { email: string }) {
    return this.httpService.post('/student/verify-activate-code/new', payload);
  }

  verifyOtp(payload: OtpVerify) {
    return this.httpService.post('/student/verify-activate-code', payload);
  }

  getOtpInfo(payload: { email: string }) {
    return this.httpService.post('/student/verify-activate-code/info', payload);
  }
}
